import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";

const draftUrl = () => resolveApiUrl("/api/register-agent/draft");
const draftAbandonUrl = () => resolveApiUrl("/api/register-agent/draft/abandon");
const billingSessionUrl = () => resolveApiUrl("/api/register-agent/billing-session");
const billingVerifyUrl = () => resolveApiUrl("/api/register-agent/billing-verify");
const trialUrl = () => resolveApiUrl("/api/register-agent/trial");

/**
 * @param {object} body
 * @param {string} body.fullName
 * @param {string} [body.firstName]
 * @param {string} [body.lastName]
 * @param {string} [body.idNumber]
 * @param {string} body.email
 * @param {string} body.phone
 * @param {string} body.password
 * @param {string} body.businessName
 * @param {number} body.packageId
 * @returns {Promise<{ draftId: string, status?: string }>}
 * @throws {Error} with .code === 'REGISTRATION_CONFLICT' and .conflictPayload for 409
 */
export async function postRegistrationDraft(body) {
  const res = await axios.post(draftUrl(), body, {
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
    validateStatus: () => true,
  });
  const { data, status } = res;
  if (status === 409 && data?.error === "registration_already_in_progress") {
    const e = new Error("registration_already_in_progress");
    e.code = "REGISTRATION_CONFLICT";
    e.conflictPayload = data;
    throw e;
  }
  if (status >= 400) {
    const msg =
      (typeof data?.message === "string" && data.message) ||
      (typeof data?.error === "string" && data.error) ||
      `שגיאת שרת (${status})`;
    const err = new Error(msg);
    err.response = { status, data };
    throw err;
  }
  return data;
}

/**
 * הרשמת ניסיון 60 יום ללא כרטיס אשראי.
 * @param {object} body
 */
export async function postTrialRegistration(body) {
  const res = await axios.post(trialUrl(), body, {
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
    validateStatus: () => true,
  });
  const { data, status } = res;
  if (status === 409 && data?.error === "registration_already_in_progress") {
    const e = new Error("registration_already_in_progress");
    e.code = "REGISTRATION_CONFLICT";
    e.conflictPayload = data;
    throw e;
  }
  if (status >= 400) {
    const msg =
      (typeof data?.message === "string" && data.message) ||
      (typeof data?.error === "string" && data.error) ||
      `שגיאת שרת (${status})`;
    const err = new Error(msg);
    err.response = { status, data };
    throw err;
  }
  return data;
}

/** @param {string} draftId */
export async function postAbandonRegistrationDraft(draftId) {
  const res = await axios.post(
    draftAbandonUrl(),
    { draftId },
    {
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
      validateStatus: () => true,
    },
  );
  const { data, status } = res;
  if (status >= 400) {
    const msg =
      (typeof data?.message === "string" && data.message) ||
      (typeof data?.error === "string" && data.error) ||
      `שגיאת שרת (${status})`;
    const err = new Error(msg);
    err.response = { status, data };
    throw err;
  }
  return data;
}

/** @param {Record<string, unknown>} snapshot no password fields */
export function saveRegistrationFormSnapshot(snapshot) {
  try {
    sessionStorage.setItem(AGENT_REG_FORM_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/** @param {{ draftId: string }} body */
export async function postBillingSession(body) {
  const { data } = await axios.post(billingSessionUrl(), body, {
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
  });
  return data;
}

/**
 * @param {string} draftId
 * @param {Record<string, string>} redirectParams flat query params from return URL (no card PAN/CVV)
 */
export async function postBillingVerify(draftId, redirectParams) {
  const url = billingVerifyUrl();
  if (String(import.meta.env.VITE_LOG_BILLING_VERIFY || "").trim() === "1") {
    console.info("[HotelsRiver FE] NEW_UID_FLOW_ACTIVE billing-verify POST →", url);
  }
  const { data } = await axios.post(
    url,
    { draftId, redirectParams },
    { headers: { "Content-Type": "application/json" }, withCredentials: true },
  );
  return data;
}

export const AGENT_REG_DRAFT_STORAGE_KEY = "hr_agent_reg_draft_id";

/** Safe subset of registration form (no passwords) — restored after failed Hyp verify */
export const AGENT_REG_FORM_SNAPSHOT_KEY = "hr_agent_reg_form_snapshot_v1";
